import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Checkbox,
  Button,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { BillItem, KITCHEN_ITEM_STATUS } from '../../../all-types';
import { getKitchenItemStatusInfo } from '../../../utils';

const getTimeFromValue = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const getInKitchenTime = (item: BillItem) => {
  const kitchInfo = item?.kitchInfo;
  const receivedTime =
    getTimeFromValue(kitchInfo?.itemReceviedTime) ||
    getTimeFromValue((kitchInfo as any)?.itemReceivedTime);

  if (receivedTime) return receivedTime;

  if (kitchInfo?.status === KITCHEN_ITEM_STATUS.IN_KITCHEN) {
    return getTimeFromValue(kitchInfo?.updatedAt);
  }

  return null;
};

const formatElapsedTime = (startedAt: number, now: number) => {
  const elapsedMs = Math.max(0, now - startedAt);
  const totalMinutes = Math.floor(elapsedMs / 60000);

  if (totalMinutes < 1) return 'Now';

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} hr`;
  return `${hours} hr ${minutes} min`;
};

export type ItemCardProps = {
  item: BillItem & { tableNo?: number; billNo?: string | number };
  selectable?: boolean;
  checked?: boolean;
  onCheckChange?: (checked: boolean) => void;
  onAdvance?: () => void;
  advanceLabel?: string; // NEW: custom label for the action button
  advanceDisabled?: boolean;
};

export const ItemCard: React.FC<ItemCardProps> = ({
  item,
  selectable,
  checked,
  onCheckChange,
  onAdvance,
  advanceLabel = 'Next',
  advanceDisabled = false,
}) => {
  const [now, setNow] = useState(() => Date.now());
  const sInfo = getKitchenItemStatusInfo(
    item?.kitchInfo?.status || KITCHEN_ITEM_STATUS.IN_KITCHEN
  );
  const inKitchenTime = useMemo(() => getInKitchenTime(item), [item]);

  useEffect(() => {
    if (!inKitchenTime) return;

    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, [inKitchenTime]);

  const receivedTimeLabel = inKitchenTime
    ? new Date(inKitchenTime).toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : '-';
  const elapsedTimeLabel = inKitchenTime
    ? formatElapsedTime(inKitchenTime, now)
    : '-';
  const elapsedTimeBadge = (
    <Box
      sx={{
        ml: 'auto',
        px: 1.5,
        py: 0.75,
        minWidth: 76,
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        border: (t) => `1px solid ${t.palette.divider}`,
        textAlign: 'center',
        boxShadow: 1,
      }}
    >
      <Typography
        variant="h6"
        fontWeight={900}
        lineHeight={1}
        color="primary.main"
      >
        {elapsedTimeLabel}
      </Typography>
    </Box>
  );

  const instructions = (() => {
    const raw = item?.kitchInfo?.kitchenInstrcutions;
    if (!raw) return '';
    try {
      const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Object.entries(obj)
        .map(([k, v]) => `${k}: ${String(v)}`)
        .join(', ');
    } catch {
      return String(raw);
    }
  })();

  return (
    <Card elevation={2} sx={{borderRadius: 3, backgroundColor: 'primary.light' }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" spacing={1.25}>
          {selectable && (
            <Checkbox
              checked={!!checked}
              onChange={(e) => onCheckChange?.(e.target.checked)}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
            />
          )}

          <Box flex={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography
                variant="subtitle1"
                fontWeight={800}
                sx={{ textTransform: 'capitalize' }}
              >
                {item.itemName}
              </Typography>
              <Chip size="small" label={sInfo.displayText} color={sInfo.color} />
            </Stack>

            <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
              <Chip size="small" label={`x${item.noOfItems} ${item.quantityType}`} />
              {item.quantity > 1 && (
                <Chip size="small" label={`${item.quantity} ${item.quantityType}`} />
              )}
              {item.tableNo && <Chip size="small" label={`Table #${item.tableNo}`} />}
              {item.billNo && <Chip size="small" label={`Bill #${item.billNo}`} />}
            </Stack>

              <Stack direction="row" spacing={1.5} mt={0.5} flexWrap="wrap">
                <Typography variant="body2" fontWeight={800}>
                  Received: {receivedTimeLabel}
                </Typography>
              </Stack>

            {instructions && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  border: (t) => `1px dashed ${t.palette.divider}`,
                }}
              >
                <Typography variant="caption" fontWeight={800} sx={{ mr: 0.5 }}>
                  Instructions:
                </Typography>
                <Typography variant="body2" component="span" sx={{ whiteSpace: 'normal' }}>
                  {instructions}
                </Typography>
              </Box>
            )}

            {/* Action */}
            {onAdvance && (
              <Stack
                direction="row"
                spacing={1.25}
                mt={1.25}
                alignItems="center"
                justifyContent="space-between"
              >
                <Button
                  onClick={onAdvance}
                  variant="contained"
                  size="large"
                  disabled={advanceDisabled}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ minHeight: 48, px: 2.25 }}
                >
                  {advanceDisabled ? 'Updating…' : advanceLabel}
                </Button>
                {elapsedTimeBadge}
              </Stack>
            )}
            {!onAdvance && (
              <Stack direction="row" justifyContent="flex-end" mt={1.25}>
                {elapsedTimeBadge}
              </Stack>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
